import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { WebShopService } from '../../services/webShop.service';
import { MAX_BASKETITEM_COUNT, WebshopSettingsDataModel2 } from '../../shared/models';
import Utils from '../../shared/utils';
import { InternationalisationService } from '../../forked/angular-internationalisation';
import { RemoteGroupOrderService } from '../../services/RemoteGroupOrder.service';
import { Article, GroupBasketItemUiData, GroupMemberUiData, GroupUiData, MemberStatus } from '../../services/IRemoteGroupOrderService';
import { IRemoteGroupBasketItem, IRemoteGroupBasketItemExtra, IRemoteGroupMember, IRemoteGroupMemberItems, IRemoteOrdeGroupMemberDetailService } from './remoteOrderGroupMemberDetail.facade';

class RemoteGroupMember implements IRemoteGroupMember {
  public basketItems_count: number;
  public basketItems_amount: number;

  constructor(private memberData: GroupMemberUiData, groupData: GroupUiData) {
    const userId = memberData.userId;
    this.basketItems_count = 0;
    this.basketItems_amount = 0;

    groupData.basket_items.forEach((item) => {
      if (item.userId !== userId) return;
      this.basketItems_count += item.count;
      this.basketItems_amount += item.count * item.price;
    });

    this.basketItems_amount = Utils.centsToCurrency(this.basketItems_amount);
  }

  public get groupId(): number {
    return this.memberData.groupId;
  }

  public get userId(): number {
    return this.memberData.userId;
  }

  public get name(): string {
    return this.memberData.name;
  }

  public get itemsCount(): number {
    return this.basketItems_count;
  }

  public get itemsAmount(): number {
    return this.basketItems_amount;
  }

  public get stateReady(): boolean {
    return this.memberData.stateReady === MemberStatus.Ready;
  }

}

class RemoteGroupBasketItemExtra implements IRemoteGroupBasketItemExtra {
  constructor(private _model: IRemoteGroupBasketItemExtra) { }

  public get title(): string {
    return InternationalisationService.locale === "de" || Utils.IsNullOrWhiteSpace(this._model.title_en)
      ? this._model.title_de
      : this._model.title_en;
  }

  public get title_de(): string {
    return this._model.title_de;
  }
  public get title_en(): string {
    return this._model.title_en;
  }

  public get id(): number {
    return this._model.id;
  }
  public set id(v: number) {
    throw new Error("Setter not implemented.");
  }

  public get subIndex(): number {
    return 0;
  }

  public get count(): number {
    return this._model.count;
  }
}

class RemoteGroupBasketItem implements IRemoteGroupBasketItem {
  public extraItems: RemoteGroupBasketItemExtra[];

  constructor(
    private _service: RemoteOrderGroupMemberDetailService,
    private _item: GroupBasketItemUiData,
  ) {
    this.extraItems = this._item.extras.map((extra) => new RemoteGroupBasketItemExtra(extra));
  }

  public get id(): number {
    return this._item.id;
  }

  public get label(): string {
    return InternationalisationService.locale === "de" || Utils.IsNullOrWhiteSpace(this._item.name_en)
      ? this._item.name_de
      : this._item.name_en;
  }

  public get valid_date(): number {
    return null;
  }

  public get deposit(): number {
    return this._item.deposit;
  }

  public get extras(): IRemoteGroupBasketItemExtra[] {
    return this.extraItems;
  }

  public get count(): number {
    return this._item.count;
  }

  public get price(): number {
    return Utils.centsToCurrency(this._item.price * this._item.count);
  }

  public get priceWithoutExtras(): number {
    return Utils.centsToCurrency(this._item.priceWithoutExtras)
  }

  public get comment(): string {
    return this._item.comment;
  }

  public async setcomment(commentValue: string) {
    this._item.comment = commentValue;
    await this._service.addComment(this._item);
  }

  public get canComment(): boolean {
    return this._service.canComment;
  }

  public get canIncrement(): boolean {
    return this._item.count < MAX_BASKETITEM_COUNT;
  }

  public increment() {
    if (this.canIncrement) {
      this._service.addCountItem(this._item.article);
    }
  }

  public decrement() {
    this._service.removeCountItem(this._item.article);
  }

  public get canDecrement(): boolean {
    return this._item.count > 0;
  }
}

class RemoteGroupMemberItems implements IRemoteGroupMemberItems {
  public items: IRemoteGroupBasketItem[];

  constructor(
    private _memberData: GroupMemberUiData,
    private _service: RemoteOrderGroupMemberDetailService,
    private _groupData: GroupUiData,
  ) {
    const userId = _memberData.userId;
    const userBasketItems = this._groupData.basket_items.filter((item) => item.userId === userId);
    this.items = userBasketItems.map((item) => new RemoteGroupBasketItem(_service, item));
  }

  public get groupId(): number {
    return this._memberData.groupId;
  }

  public get userId(): number {
    return this._memberData.userId;
  }
}

@Injectable()
export class RemoteOrderGroupMemberDetailService implements IRemoteOrdeGroupMemberDetailService, OnDestroy {
  private _webshopSettingsChangedSub: Subscription;
  private _membersChangedSubscription: Subscription;
  private _itemsChangedSubscription: Subscription;
  private _groupStatusChangedSubscription: Subscription;

  private _webshopSettings: WebshopSettingsDataModel2;

  public canComment: boolean = false;
  private _isOwner: boolean = false;
  public get isOwner() {
    return this._isOwner;
  }

  private _groupMember: RemoteGroupMember;
  private _groupMemberSubject: BehaviorSubject<IRemoteGroupMember> = new BehaviorSubject<IRemoteGroupMember>(null);
  public get groupMember$(): Observable<IRemoteGroupMember> {
    return this._groupMemberSubject.asObservable();
  }

  private _groupMemberItems: RemoteGroupMemberItems[] = [];
  private _groupMemberItemsSubject: BehaviorSubject<IRemoteGroupMemberItems[]> = new BehaviorSubject<IRemoteGroupMemberItems[]>([]);
  public get groupMemberItems$(): Observable<IRemoteGroupMemberItems[]> {
    return this._groupMemberItemsSubject.asObservable();
  }

  private _openPriceSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public get open_price$(): Observable<number> {
    return this._openPriceSubject.asObservable();
  }

  private _itemCountSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public get item_count$(): Observable<number> {
    return this._itemCountSubject.asObservable();
  }

  public get groupName() {
    return this._remoteGroupOrderService.groupName;
  }

  public get groupCreator() {
    return this._remoteGroupOrderService.creatorName;
  }

  public get paymentStatusFinishing$(): Observable<boolean> {
    return this._remoteGroupOrderService.groupStatusPaymentStarted$;
  }

  constructor(
    private _remoteGroupOrderService: RemoteGroupOrderService,
    private _webShopService: WebShopService
  ) {
    const me = this;
    me._webshopSettingsChangedSub = me._webShopService.webshopSettingsDataModelObserveChanges.subscribe(async (_) => {
      const webshopSetting = await me._webShopService.getWebshopSettingsDataModel();
      me.rebindWebshopSetting(webshopSetting);
    });
  }

  ngOnDestroy(): void {
    if (this._webshopSettingsChangedSub)
      this._webshopSettingsChangedSub.unsubscribe();
    if (this._membersChangedSubscription)
      this._membersChangedSubscription.unsubscribe();
    if (this._itemsChangedSubscription)
      this._itemsChangedSubscription.unsubscribe();
    if (this._groupStatusChangedSubscription)
      this._groupStatusChangedSubscription.unsubscribe();
  }

  public async initial(currentUserId: string): Promise<void> {
    const me = this;
    const webshopSetting = await me._webShopService.getWebshopSettingsDataModel();
    me.rebindWebshopSetting(webshopSetting);

    me._membersChangedSubscription = me._remoteGroupOrderService.membersChanged$.subscribe(async () => {
      await me.reloadGroupData(currentUserId);
    });
    me._itemsChangedSubscription = me._remoteGroupOrderService.itemsChanged$.subscribe(async () => {
      await me.reloadGroupData(currentUserId);
    });
    me._groupStatusChangedSubscription = me._remoteGroupOrderService.groupStatusChanged$.subscribe(async () => {
      await me.reloadGroupData(currentUserId);
    });
    await me.reloadGroupData(currentUserId);
  }

  private rebindWebshopSetting(webshopSetting: WebshopSettingsDataModel2) {
    const me = this;
    me._webshopSettings = webshopSetting;
    me.canComment = me._webshopSettings.use_item_annotation;
  }

  /** render all group Information */
  private async reloadGroupData(currentUserId: string): Promise<void> {
    const me = this;
    const groupData = await me._remoteGroupOrderService.getGroupData();
    await me.loadMemberData(groupData, currentUserId);
  }

  private async loadMemberData(groupData: GroupUiData, currentUserId: string): Promise<void> {
    const me = this;
    // Check if the current user is the owner
    me._isOwner = me._remoteGroupOrderService.groupManageData.userId.toString() === currentUserId;
    // Find the current user's member data
    const memberData = groupData.members.find(member => member.userId.toString() === currentUserId);
    if (!memberData) {
      throw new Error(`Member with userId ${currentUserId} not found.`);
    }
    // Initialize group member
    me._groupMember = new RemoteGroupMember(memberData, groupData);
    me._groupMemberSubject.next(me._groupMember);
    // Filter members with basket items
    const membersWithBasketItems = groupData.members.filter(member => member.userId.toString() === currentUserId);
    me._groupMemberItems = membersWithBasketItems.map(member => new RemoteGroupMemberItems(member, me, groupData));
    me._groupMemberItemsSubject.next(me._groupMemberItems);
    // Calculate total count and amount of group basket items for the current user
    let groupBasketItemsCount = 0;
    let groupBasketItemsAmount = 0;
    groupData.basket_items
      .filter(item => item.userId.toString() === currentUserId)
      .forEach(item => {
        groupBasketItemsCount += item.count;
        groupBasketItemsAmount += item.count * item.price;
      });

    // Update observables with the calculated values
    me._itemCountSubject.next(groupBasketItemsCount);
    me._openPriceSubject.next(Utils.centsToCurrency(groupBasketItemsAmount));
  }

  public async addComment(item: GroupBasketItemUiData) {
    const me = this;
    if (item.comment !== item.article.Comment) {
      item.article.Comment = item.comment;
    }
    await me._remoteGroupOrderService.addComment(item.article);
  }

  public async addCountItem(item: Article): Promise<void> {
    const me = this;
    item.Count = item.Count + 1;
    await me._remoteGroupOrderService.PutArticle(me._remoteGroupOrderService.groupId, me._remoteGroupOrderService.userId, item);
  }

  public async removeCountItem(item: Article): Promise<void> {
    const me = this;
    if (item.Count - 1 == 0) {
      // Count muss dem entsprechen der in der gruppe ist
      await me._remoteGroupOrderService.DeleteArticle(me._remoteGroupOrderService.groupId, me._remoteGroupOrderService.userId, item);
    } else {
      item.Count = item.Count - 1;
      await me._remoteGroupOrderService.PutArticle(me._remoteGroupOrderService.groupId, me._remoteGroupOrderService.userId, item);
    }
  }

  /** change state */
  public async changeStateReady(userId: number, state: boolean) {
    const me = this;
    await me._remoteGroupOrderService.changeAndSetUserStateReady(userId, state);
  }

}